// Libraries
import { Flex, Text } from '@chakra-ui/react';
// Components
import { Checkbox } from '@atoms/index';
import { FilterGroupsType } from '@organisms/index';
// Utils
import text from '@translations/en.json';
import { RatingsMenuProps } from './RatingsMenu.types';

/**
 * `RatingsMenu` component provides a custom interface for displaying and interacting with a list of ratings.
 * It renders a menu section with a title and a "Select All" checkbox, followed by additional children elements.
 *
 * @component
 * @param {RatingsMenuProps} props - The props to configure the RatingsMenu component.
 * @param {ReactNode} props.children - Additional React elements to be displayed in the menu, typically a list of individual rating options.
 * @param {boolean} props.isPartiallyFiltered - Indicates whether some but not all rating options are selected.
 * @param {Function} props.handleSelectAll - Function to handle the "Select All" checkbox click, typically toggling selection of all rating options.
 * @param {boolean} props.usedInDropdown - Flag to tell the component is being displayed inside the dropdown.
 *
 * @returns {ReactElement} The rendered RatingsMenu component.
 */
export const RatingsMenu = ({
  children,
  isPartiallyFiltered,
  handleSelectAll,
  usedInDropdown = false,
}: RatingsMenuProps) => {
  const p = usedInDropdown ? 2 : 4;
  return (
    <Flex direction='column' key={FilterGroupsType.CII_RATING}>
      <Flex align='center' w='full' p={p} justify='space-between'>
        {!usedInDropdown && (
          <Text fontSize='m' fontWeight='medium'>
            {text.cii_rating}
          </Text>
        )}
        <Flex gap={2} align='center'>
          <Checkbox
            isSelected={true}
            isPartial={isPartiallyFiltered}
            label={text.all_cii_rating}
            onClick={handleSelectAll}
          />
          <Text fontSize='m'>{text.all_cii_rating}</Text>
        </Flex>
      </Flex>
      <Flex w='full'>{children}</Flex>
    </Flex>
  );
};
